"use client";

import React, { createContext, useContext, useReducer, useCallback, useEffect, ReactNode, useRef } from "react";
import { WebSocketState, WebSocketAction, AgentRequest, GenericWebSocketMessage, ConnectionStatus } from "../types";
import { websocketReducer, initialWebSocketState } from "../reducers/websocketReducer";
import { getWebSocketUrl } from "../utils/websocketUtils";

enum ReadyState {
  UNINSTANTIATED = -1,
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

interface WebSocketContextValue {
  state: WebSocketState;
  dispatch: React.Dispatch<WebSocketAction>;

  sendMessage: (message: any) => void;
  clearMessages: (by: (message: GenericWebSocketMessage) => boolean) => void;

  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;

  // Connection status
  isReady: boolean;
  readyState: ReadyState;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);
WebSocketContext.displayName = "WebSocketContext";

interface WebSocketProviderProps {
  children: ReactNode;
  url?: string;
  enabled?: boolean;
}


export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
  enabled = true
}) => {
  const [state, dispatch] = useReducer(websocketReducer, initialWebSocketState);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const readyStateRef = useRef<ReadyState>(ReadyState.UNINSTANTIATED);

  const socketUrl = url || getWebSocketUrl();
  const shouldConnect = enabled && socketUrl;

  const createConnection = useCallback(() => {
    if (!shouldConnect || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      console.log("🔌 Attempting WebSocket connection to:", socketUrl);
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      readyStateRef.current = ReadyState.CONNECTING;

      dispatch({ type: "CONNECTION_STATUS_CHANGED", payload: "Connecting" });

      ws.onopen = () => {
        console.log("✅ WebSocket connected successfully to:", socketUrl);
        readyStateRef.current = ReadyState.OPEN;
        dispatch({ type: "CONNECTION_STATUS_CHANGED", payload: "Open" });
        dispatch({ type: "RESET_RECONNECT_ATTEMPTS" });
      };

      ws.onclose = (event: CloseEvent) => {
        console.log("❌ WebSocket disconnected:", event.reason, "Code:", event.code);
        readyStateRef.current = ReadyState.CLOSED;
        dispatch({ type: "CONNECTION_STATUS_CHANGED", payload: "Closed" });

        // Auto-reconnect logic
        if (event.code !== 1000 && state.reconnectAttempts < state.maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, state.reconnectAttempts), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            dispatch({ type: "RECONNECT_ATTEMPT" });
            createConnection();
          }, delay);
        }
      };

      ws.onerror = (event: Event) => {
        console.error("🚨 WebSocket error occurred:", event);
        console.error("WebSocket URL:", socketUrl);
        console.error("ReadyState:", ws.readyState);
        dispatch({
          type: "ERROR_OCCURRED",
          payload: "WebSocket connection error"
        });
      };

      ws.onmessage = (event: MessageEvent) => {
        console.log("📨 Raw WebSocket message received:", event.data);
        try {
          const message: GenericWebSocketMessage = JSON.parse(event.data);
          console.log("📨 Parsed WebSocket message:", message);
          dispatch({ type: "MESSAGE_RECEIVED", payload: message });
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error, "Raw data:", event.data);
        }
      };

    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      dispatch({
        type: "ERROR_OCCURRED",
        payload: "Failed to create WebSocket connection"
      });
    }
  }, [shouldConnect, socketUrl, state.reconnectAttempts, state.maxReconnectAttempts]);

  useEffect(() => {
    if (shouldConnect) {
      createConnection();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
    };
  }, [createConnection, shouldConnect]);

  const sendMessage = useCallback((message: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
    }
  }, []);

  const clearMessages = useCallback((by: (message: GenericWebSocketMessage) => boolean = (message: GenericWebSocketMessage) => true) => {
    dispatch({ type: "CLEAR_MESSAGES", payload: by });
  }, []);


  const connect = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      createConnection();
    }
  }, [createConnection]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "Manual disconnect");
    }
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(connect, 1000);
  }, [connect, disconnect]);

  const contextValue: WebSocketContextValue = {
    state,
    dispatch,
    sendMessage,
    clearMessages,
    connect,
    disconnect,
    reconnect,
    isReady: state.isConnected,
    readyState: readyStateRef.current,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook
export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};
